import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Header, Modal, Button } from 'semantic-grab-ui';
import { Icon, Row, Col, Form, Divider, Input } from 'grab-ui';
import type { StoreState } from 'reducers';
import type { RequestStates } from 'types/redux';
import moment from 'moment';
import _ from 'lodash';

import scribe from '@grab-x/scribe-web-sdk';
import ApproverSelectionInput from 'omega-rtc/views/trident-campaigns/review/ApproverSelectionInput';
import {
  getApprovalFlowConfig,
  doCampaignApprovalFlowAction,
  campaignApprovalFlowActions,
} from 'actions/newTridentExtra';
import {
  CampaignChangeType,
  CampaignStatus,
  convertTridentActionToHedwigChannel,
  UcdCampaignResourceName,
  UcdUniversalScope,
} from 'data/trident-campaigns';
import { processCampaignData } from 'omega-rtc/utils/scribe';
import ScribeEventName from 'constants/scribe';
import { isRequestsPending, hasJustSuccess } from 'utils/request-state-utils';
import { getSegmentUrl, getHedwigTemplateUrl } from 'views/trident-campaigns/review/campaign-analysis';
import { TRIDENT_CAMPAIGN_TEMPLATE_TYPES as CAMPAIGN_TEMPLATE_TYPES, TRIDENT_CAMPAIGN_TEMPLATE } from 'constants/ucd';
import { hasPermission } from 'omega-rtc/utils/user-utils';
import './CampaignApprovalRequestForm.scss';

type Props = {
  closeModal: Function,
  doCampaignApprovalFlowAction: Function,
  approvalFlowRequest: RequestStates,
  campaignView: Object,
  user: UserState,
  userPermissions: Object,
  conditionDetails: Object,
  actionDetails: Object,
  segmentDetails: Object,
  hedwigDetails: Object,
  getLinker: Function,
  onApprove: Function,
  editingUrl: string, // to store specially url when canceling modal
}

class CampaignApprovalRequestForm extends Component {
  props: Props;
  constructor(props: Props) {
    super(props);
    this.state = {
      comment: '',
      approvers: [],
    };
  }

  componentDidMount() {
    this.props.getApprovalFlowConfig();
  }

  componentWillReceiveProps(nextProps: Props) {
    if (hasJustSuccess(this.props.approvalFlowRequest, nextProps.approvalFlowRequest)) {
      sessionStorage.setItem('toastrMessage', 'Request for Approval is sent');
      window.location.reload();
    }
  }

  handleApproverChange = (_: any, data: any) => {
    this.setState({
      approvers: data.value,
    });
  };

  handleCommentChange = (e) => {
    this.setState({
      comment: e.target.value,
    });
  };

  handleSendForApproval = () => {
    const { countryID, campaignID: ucdCampaignID } = _.get(this.props, 'campaignView', {});
    const campaignID = !_.isEmpty(ucdCampaignID) ? ucdCampaignID.replace('trident_', '') : ucdCampaignID;
    this.props.doCampaignApprovalFlowAction(countryID, campaignID, campaignApprovalFlowActions.request, {
      adminEmail: this.props.user.email,
      comment: this.state.comment,
      templateIDs: [], // TODO: need to figure out why need to pass empty array here.
      notifyees: this.state.approvers,
      allowedApprovers: this.state.approvers,
    });

    scribe.sendEvent(
      ScribeEventName.CAMPAIGN_REQUESTED,
      processCampaignData(
        { ...this.props.campaign, user: this.props.user },
        { approvers: this.state.approvers, comment: this.state.comment },
      ),
    );

    // in case, the requester is the approver
    setTimeout(() => {
      scribe.sendEvent(
        ScribeEventName.FORM_OPEN,
        processCampaignData(
          { ...this.props.campaign, user: this.props.user },
          { approvers: this.state.approvers, comment: this.state.comment },
        ),
      );
    }, 10);
    if (this.props.onApprove !== undefined) {
      this.props.onApprove();
    }
  };

  // return list of users that have created or updated the campaign before
  getPastEditors() {
    const records = _.get(this.props.campaignView, 'edits', []);
    const editors = new Set();
    _.map(records, r => {
      const changeType = _.get(r, 'change.type');
      if (changeType === CampaignChangeType.create || changeType === CampaignChangeType.update) {
        editors.add(r.editor);
      }
    });
    return [...editors];
  }

  handleBackToEditing = () => {
    const isJourneyCampaign =
      _.get(this.props, 'campaignView.campaign.templateType', '') ===
        TRIDENT_CAMPAIGN_TEMPLATE.multistepJourney ||
      window.location.href.includes('journey_campaign');
    if (isJourneyCampaign) {
      window.location.href = this.props.editingUrl;
    } else {
      let redirectURL = window.location.href.replace('view', 'trident/edit');
      if (_.isEmpty(_.get(this.props.campaignView, 'campaign.templateType'))) {
        redirectURL = _.get(this.props.campaignView, 'campaign.link');
      }
      window.location.href = redirectURL;
    }
  };

  render() {
    const {
      approvalFlowConfig,
      campaignView,
      segmentDetails,
      getLinker,
      hedwigDetails,
      conditionDetails,
      actionDetails,
      closeModal,
    } = this.props;
    const allowSelfApprove = _.get(approvalFlowConfig, 'allowSelfApprove', true);
    const pastEditors = this.getPastEditors();
    const excludedApprovers = allowSelfApprove ? [] : pastEditors;

    const { audience, status, campaign } = campaignView;
    const allowSendForApproval = hasPermission(this.props.userPermissions, UcdCampaignResourceName, UcdUniversalScope, 'update')
      && status === CampaignStatus.draft;

    const isApprovalFlowRequestOngoing = isRequestsPending(this.props.approvalFlowRequest);

    const segmentUserType = audience.toUpperCase();

    const templateType = _.get(campaign, 'templateType', '');
    const selectedTemplate = _.find(CAMPAIGN_TEMPLATE_TYPES, { value: templateType });
    const templateTitle = _.get(selectedTemplate, 'title');

    return (
      <Modal className={'approval-request-modal'} size={'small'} open>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <Header>
              Campaign Summary
            </Header>
          </div>
          <div className={'close-modal'} onClick={closeModal}>
            <Icon color={'#707070'} type="close" style={{ fontSize: 21 }} />
          </div>
        </div>
        <div style={{ marginTop: 36 }} />
        <Modal.Content style={{ padding: 0 }}>
          <Row gutter={24}>
            {!_.isEmpty(templateTitle) &&
              <Col span={12}>
                <Form.Item
                  label={'Trident Template'}
                >
                  <span>{templateTitle}</span>
                </Form.Item>
              </Col>
            }
            {!_.isEmpty(segmentDetails) &&
              <Col span={12}>
                <Form.Item
                  label={'Segment'}
                >
                  {_.map(segmentDetails, (item) => (
                    getLinker(item.label, getSegmentUrl(segmentUserType, item.ID))
                  ))}
                </Form.Item>
              </Col>
            }
            {!_.isEmpty(hedwigDetails) &&
              <Col span={12}>
                <Form.Item
                  label={'Hedwig'}
                >
                  {_.map(hedwigDetails, (item) => (
                    getLinker(item.ID, getHedwigTemplateUrl(convertTridentActionToHedwigChannel(item.type), item.ID))
                  ))}
                </Form.Item>
              </Col>
            }
            {!_.isEmpty(conditionDetails) &&
              <Col span={12}>
                <Form.Item
                  label={'Condition'}
                >
                  {_.map(conditionDetails, (item) => (
                    <div>{item.label} </div>
                  ))}
                </Form.Item>
              </Col>
            }
            {!_.isEmpty(actionDetails) &&
              <Col span={12}>
                <Form.Item
                  label="Action to Triger"
                  style={{ marginBottom: 16 }}
                >
                  {_.map(actionDetails, (item) => (
                    <div>{item.label}</div>
                  ))}
                </Form.Item>
              </Col>
            }
            <Col span={12}>
              <Form.Item
                label={'Date'}
              >
                <span>{moment(_.get(campaignView, 'campaign.start')).format('DD MMM YYYY')}</span>
              </Form.Item>
            </Col>
            {/* TODO: for this time field. Will add later. */}
            {/* <Col span={12}> */}
            {/*  <Form.Item */}
            {/*    label={'Time'} */}
            {/*  > */}
            {/*    <span>07:15 GMT+7</span> */}
            {/*  </Form.Item> */}
            {/* </Col> */}
          </Row>
          <Divider />
          {
            allowSendForApproval &&
            <React.Fragment>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item className={'approver-input-wrapper'}>
                    <label>Approver</label>
                    <ApproverSelectionInput
                      onChange={this.handleApproverChange}
                      excludeList={excludedApprovers}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item className={'comment-input-wrapper'}>
                    <label>Comment <span className={'minor-label'}>(Optional)</span></label>
                    <Input name={'comment'} placeholder="Comment for Approval" onChange={this.handleCommentChange} />
                  </Form.Item>
                </Col>
              </Row>
            </React.Fragment>
          }
        </Modal.Content>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '100%',
              }}
              className={'back-icon-button-color'}
            />
          </div>
          <div>
            <Button className={'editing-btn'} onClick={this.handleBackToEditing}>Go back to Editing</Button>
            <Button
              className={'send-approval-btn'}
              primary
              disabled={_.isEmpty(this.state.approvers)}
              loading={isApprovalFlowRequestOngoing}
              onClick={this.handleSendForApproval}
            >Send for Approval</Button>
          </div>
        </div>
      </Modal>
    );
  }
}

function mapStateToProps(state: StoreState, ownProps: Object) {
  const entityState = _.get(state, ownProps.stateKey, {});
  return {
    userPermissions: state.app.user.userPermissions,
    user: state.app.user,
    campaign: entityState.active,
    approvalFlowRequest: _.get(state.requests, 'campaignApprovalFlowRequest', {}),
    approvalFlowConfig: _.get(state, 'newTridents.approvalFlowConfig', {}),
  };
}

export default connect(mapStateToProps, {
  getApprovalFlowConfig,
  doCampaignApprovalFlowAction,
})(CampaignApprovalRequestForm);
